import store from '../../store'
import { auth } from '../../firebase'
import { IABF_DANCER_YEAR } from '../../config/iabf-event'

// Full admin: sees finance summaries + everything else.
// Limited admin: same backend access but finance summaries are hidden.
const FINANCE_ADMIN_EMAIL = 'julie@brocheballet.com'
const LIMITED_ADMIN_EMAILS = ['jen@redaccordionstudios.com']
const ALL_ADMIN_EMAILS = [FINANCE_ADMIN_EMAIL, ...LIMITED_ADMIN_EMAILS]

function normEmail (email) {
  return String(email || '').trim().toLowerCase()
}

export function isAdminEmail (email) {
  return ALL_ADMIN_EMAILS.includes(normEmail(email))
}

export function isFinanceAdminEmail (email) {
  return normEmail(email) === FINANCE_ADMIN_EMAIL
}

function getUser () {
  const storeUser = store.getters.user
  if (storeUser) return Promise.resolve(storeUser)

  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      unsubscribe()
      if (firebaseUser) {
        const user = { id: firebaseUser.uid, email: firebaseUser.email }
        store.commit('setUser', user)
        resolve(user)
      } else {
        resolve(null)
      }
    })
  })
}

export async function adminGuard (to, from, next) {
  const user = await getUser()
  if (!user) {
    next('/sign-in?redirect=' + encodeURIComponent(to.fullPath))
  } else if (!isAdminEmail(user.email)) {
    next('/')
  } else {
    // Admin pages share the IABF layout (so the sidebar shows up). Individual
    // pages can still override via this.$store.commit('setLayout', '…') if
    // they need a different chrome.
    store.commit('setLayout', 'iabf-layout')
    next()
  }
}

export async function financeAdminGuard (to, from, next) {
  const user = await getUser()
  if (!user) {
    next('/sign-in?redirect=' + encodeURIComponent(to.fullPath))
  } else if (!isFinanceAdminEmail(user.email)) {
    next('/iabf/admin')
  } else {
    store.commit('setLayout', 'iabf-layout')
    next()
  }
}

export async function dancerGuard (to, from, next) {
  const user = await getUser()
  if (!user) {
    next('/')
  } else {
    next()
  }
}

function isAlumniOrCanceledDancer () {
  const dancer = store.getters.currentIABFDancer || {}
  if (dancer.isCanceledAccount) {
    return true
  }
  let y = Number(dancer.festivalYear)
  if (!Number.isFinite(y) || y <= 0) {
    if (store.state.masqueradeDancerId) {
      y = Number(store.state.adminFestivalYear)
    } else {
      y = Number(store.state.iabfDancerYear)
    }
  }
  return Number.isFinite(y) && y > 0 && y < IABF_DANCER_YEAR
}

async function hydrateDancer (user) {
  if (store.state.masqueradeDancerId) {
    try {
      await store.dispatch('refreshEffectiveDancer')
    } catch (e) { /* snapshot still works */ }
    return store.getters.currentIABFDancer
  }
  let dancer = store.getters.currentIABFDancer
  if (dancer && dancer.id) {
    return dancer
  }
  try {
    if (user && user.id) {
      await store.dispatch('loadIABFDancerByUid', user.id)
    }
    dancer = store.getters.currentIABFDancer
    if ((!dancer || !dancer.id) && user && user.email) {
      await store.dispatch('linkIABFDancerByEmail', {
        email: user.email,
        uid: user.id
      })
    }
  } catch (e) { /* stay logged in; pages handle empty dancer */ }
  return store.getters.currentIABFDancer
}

/** Current-year prep only — 2026 alumni and canceled accounts go home. */
export async function dancerPrepGuard (to, from, next) {
  const user = await getUser()
  if (!user) {
    next('/')
    return
  }
  await hydrateDancer(user)
  if (isAlumniOrCanceledDancer()) {
    next({ path: '/', replace: true })
    return
  }
  next()
}
